import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Megaphone, Plus, Pencil, Trash2, Loader2, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const api = useApi();
  const [announcements, setAnnouncements] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedClinicIds, setSelectedClinicIds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    scope: user?.role === 'clinic_admin' ? 'specific_clinic' : 'all_clinics',
    start_date: new Date().toISOString().slice(0, 16),
    end_date: '',
    show_popup: true,
    is_dismissible: true
  });

  useEffect(() => {
    loadAnnouncements();
    if (user?.role === 'superadmin') {
      loadClinics();
    }
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await api.get('/announcements?active_only=false');
      setAnnouncements(data);
    } catch (error) {
      console.error('Failed to load announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const loadClinics = async () => {
    try {
      const data = await api.get('/clinics');
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for specific clinic selection
    if (formData.scope === 'specific_clinic' && selectedClinicIds.length === 0) {
      toast.error('Please select at least one clinic');
      return;
    }
    
    setLoading(true);

    try {
      const basePayload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      if (editingId) {
        await api.put(`/announcements/${editingId}`, basePayload);
        toast.success('Announcement updated successfully');
      } else {
        // For specific clinics, create separate announcements for each selected clinic
        if (formData.scope === 'specific_clinic' && selectedClinicIds.length > 0) {
          const promises = selectedClinicIds.map(clinicId => 
            api.post('/announcements', { ...basePayload, clinic_id: clinicId })
          );
          await Promise.all(promises);
          toast.success(`Announcement created for ${selectedClinicIds.length} clinic(s)`);
        } else {
          // All clinics
          await api.post('/announcements', basePayload);
          toast.success('Announcement created successfully');
        }
      }

      setDialogOpen(false);
      resetForm();
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (announcement) => {
    setEditingId(announcement.id);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      scope: announcement.scope,
      start_date: new Date(announcement.start_date).toISOString().slice(0, 16),
      end_date: announcement.end_date ? new Date(announcement.end_date).toISOString().slice(0, 16) : '',
      show_popup: announcement.show_popup,
      is_dismissible: announcement.is_dismissible
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted successfully');
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setSelectedClinicIds([]);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      scope: user?.role === 'clinic_admin' ? 'specific_clinic' : 'all_clinics',
      start_date: new Date().toISOString().slice(0, 16),
      end_date: '',
      show_popup: true,
      is_dismissible: true
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info': return <Info className="h-4 w-4 text-blue-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'urgent': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'warning': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeCardBorder = (type) => {
    switch (type) {
      case 'info': return 'border-l-4 border-l-blue-500';
      case 'warning': return 'border-l-4 border-l-yellow-500';
      case 'urgent': return 'border-l-4 border-l-red-500';
      default: return '';
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#652D90" }}>
              Announcements
            </h1>
            <p className="text-gray-600 mt-1">
              Manage announcements for {user?.role === 'clinic_admin' ? 'your clinic' : 'all clinics'}
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            style={{ backgroundColor: "#652D90" }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Announcement
          </Button>
        </div>

        {loading && !dialogOpen ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement) => (
              <Card key={announcement.id} className={getTypeCardBorder(announcement.type)}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getTypeIcon(announcement.type)}
                        <h3 className="font-semibold text-lg">{announcement.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded border ${getTypeBadgeColor(announcement.type)}`}>
                          {announcement.type.toUpperCase()}
                        </span>
                        {announcement.scope === 'all_clinics' && (
                          <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                            ALL CLINICS
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-3">{announcement.message}</p>
                      <div className="flex gap-4 text-sm text-gray-500">
                        <span>Start: {new Date(announcement.start_date).toLocaleString()}</span>
                        {announcement.end_date && (
                          <span>End: {new Date(announcement.end_date).toLocaleString()}</span>
                        )}
                        <span>{announcement.show_popup ? '🔔 Popup' : '📌 Banner only'}</span>
                        <span>{announcement.is_dismissible ? '✕ Dismissible' : '📌 Sticky'}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {(user?.role === 'superadmin' || announcement.created_by === user?.id) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(announcement)}
                            data-testid={`announcement-edit-${announcement.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(announcement.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`announcement-delete-${announcement.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {announcements.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center text-gray-500">
                  <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No announcements yet. Create one to get started!</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Edit Announcement' : 'Create Announcement'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <Label>Message *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  placeholder="Enter announcement message"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {user?.role === 'superadmin' && (
                  <div>
                    <Label>Scope</Label>
                    <Select value={formData.scope} onValueChange={(val) => {
                      setFormData({ ...formData, scope: val });
                      if (val === 'all_clinics') {
                        setSelectedClinicIds([]);
                      }
                    }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_clinics">All Clinics</SelectItem>
                        <SelectItem value="specific_clinic">Specific Clinics</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Clinic Selection for Superadmin */}
              {user?.role === 'superadmin' && formData.scope === 'specific_clinic' && (
                <div className="border rounded p-3 max-h-48 overflow-y-auto">
                  <Label className="mb-2 block">Select Clinics *</Label>
                  <label className="flex items-center gap-2 py-1 cursor-pointer border-b mb-2 pb-2">
                    <Checkbox
                      checked={selectedClinicIds.length === clinics.length && clinics.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedClinicIds(clinics.map(c => c.id));
                        } else {
                          setSelectedClinicIds([]);
                        }
                      }}
                    />
                    <span className="text-sm font-semibold">Select All ({clinics.length} clinics)</span>
                  </label>
                  {clinics.map(clinic => {
                    const duplicateCount = clinics.filter(c => c.name === clinic.name).length;
                    const displayName = duplicateCount > 1 
                      ? `${clinic.name} (${clinic.id.slice(-4)})`
                      : clinic.name;
                    
                    return (
                      <label key={clinic.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50">
                        <Checkbox
                          checked={selectedClinicIds.includes(clinic.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedClinicIds([...selectedClinicIds, clinic.id]);
                            } else {
                              setSelectedClinicIds(selectedClinicIds.filter(id => id !== clinic.id));
                            }
                          }}
                        />
                        <span className="text-sm">{displayName}</span>
                      </label>
                    );
                  })}
                  {selectedClinicIds.length > 0 && (
                    <p className="text-xs text-gray-600 mt-2">
                      {selectedClinicIds.length} clinic{selectedClinicIds.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>End Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.show_popup}
                    onChange={(e) => setFormData({ ...formData, show_popup: e.target.checked })}
                  />
                  <span className="text-sm">Show as popup on login</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_dismissible}
                    onChange={(e) => setFormData({ ...formData, is_dismissible: e.target.checked })}
                  />
                  <span className="text-sm">Allow users to dismiss</span>
                </label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} style={{ backgroundColor: "#652D90" }}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
